<template>
  <div class="material-property-edit">
    <SetPopup ref="popup" />

    <!-- 알림 다이얼로그 -->
    <v-dialog v-model="alertDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="headline">알림</v-card-title>
        <v-card-text>
          <div style="white-space: pre-line">{{ alertMessage }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="handleAlertConfirm">확인</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="service">
      <!-- 원료 정보 표시 영역 -->
      <v-form ref="materialEditForm" lazy-validation>
        <div class="filter-box filter-box--row">
          <ul class="filter-list_free">
            <li>
              <h4>원료</h4>
              <v-text-field outlined dense readonly :value="editData.material" placeholder="원료명" class="material-readonly"></v-text-field>
            </li>
          </ul>
          <div class="btn-box">
            <v-card-actions class="mr-0">
              <v-btn depressed class="btn-reset" @click="cancel">취소</v-btn>
            </v-card-actions>
            <v-card-actions>
              <v-btn depressed class="btn-search" @click="save">수정</v-btn>
            </v-card-actions>
          </div>
        </div>
      </v-form>

      <!-- 수정 폼 영역 -->
      <div class="edit-section">
        <RealGrid :domName="grid" ref="grid" :settings="settings" />
      </div>

      <!-- 히스토리 영역 -->
      <div class="history-section">
        <h4 class="section-title">변경 이력</h4>
        <RealGrid :domName="historyGrid" ref="historyGrid" :settings="historySettings" />
      </div>
    </div>
  </div>
</template>

<script>
import { columns, fields, loadCategoryData, updateCategoryColumn } from '@/assets/grid/materialPropertyEdit';
import { columns as historyColumns, fields as historyFields } from '@/assets/grid/materialPropertyHistory';
import SetPopup from '@/components/SetPopup.vue';
import RealGrid from '@/components/RealGrid.vue';
import { materialPropertyUpdate, getRawMaterialHistory } from 'api/materialProperty/materialProperty';
import { mapMutations, mapState } from 'vuex';

export default {
  props: {
    editData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isFetching: false,
      grid: 'materialPropertyEdit',
      historyGrid: 'materialPropertyHistory',
      settings: {
        columns,
        fields,
        height: 200,
        hideCheckBar: true, // 체크박스 숨김
        errorMessage: '수정할 원료 특성 데이터가 없습니다',
      },
      historySettings: {
        columns: historyColumns,
        fields: historyFields,
        height: 300,
        hideCheckBar: true,
        errorMessage: '히스토리 데이터가 없습니다',
      },
      gridData: [],
      historyPagination: {
        currentPage: 1,
        pageSize: 10000,
        totalRows: 0,
      },
      alertDialog: false,
      alertMessage: '',
      alertCallback: null,
    };
  },

  computed: {
    ...mapState('member', ['userInfo']),
  },

  watch: {
    editData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.initializeGrid();
        }
      },
      immediate: true,
    },
  },

  async mounted() {
    // Account.vue 방식으로 팝업 설정
    this.SET_POPUP({
      title: '알림',
      width: 300,
      customApprove: true,
    });

    // 카테고리 데이터 로드
    await this.loadCategories();
  },

  methods: {
    ...mapMutations('popup', ['SET_POPUP', 'SET_POPUP_TEXT']),

    async loadCategories() {
      try {
        // 카테고리 데이터 로드
        await loadCategoryData();
        // 컬럼 업데이트
        updateCategoryColumn();
      } catch (error) {
        console.error('카테고리 로드 실패:', error);
      }
    },

    initializeGrid() {
      if (this.editData && Object.keys(this.editData).length > 0) {
        // 기존 데이터를 그리드 형태로 변환
        const gridRow = {
          category: this.editData.category || '',
          material: this.editData.material || '',
          mw: this.editData.mw || '',
          density: this.editData.density || '',
          remark: this.editData.remark || '',
          reg_date: this.editData.reg_date || '',
          reg_user: this.editData.reg_user || '',
        };

        this.gridData = [gridRow];

        // Account.vue 방식으로 데이터 로드
        setTimeout(() => {
          if (this.$refs.grid && typeof this.$refs.grid.loadData === 'function') {
            this.$refs.grid.loadData(this.gridData);
          }

          // 히스토리 로드
          this.loadHistory();
        }, 300);
      }
    },

    async loadHistory(page = 1) {
      if (!this.editData.material) {
        return;
      }

      try {
        const response = await getRawMaterialHistory({
          material: this.editData.material,
          currentPage: page,
          pageSize: this.historyPagination.pageSize,
        });

        // 응답 구조에 따라 데이터 추출
        const data = response.data.data || response.data || {};
        const historyItems = data.items || [];

        // 페이징 정보 업데이트
        this.historyPagination.currentPage = page;
        this.historyPagination.totalRows = data.totalRows || historyItems.length;

        // 히스토리 그리드에 데이터 로드
        if (this.$refs.historyGrid && typeof this.$refs.historyGrid.loadData === 'function') {
          this.$refs.historyGrid.loadData(historyItems);
        }
      } catch (error) {
        console.error('히스토리 조회 실패:', error);
      }
    },

    handlePageChange(page) {
      this.loadHistory(page);
    },

    validate() {
      const rows = this.$refs.grid.getJsonRows();

      if (rows.length === 0) {
        this.showPopup('수정할 데이터가 없습니다.');
        return false;
      }

      const row = rows[0];
      const errors = [];

      // 필수 필드 검증
      if (!row.category || row.category.trim() === '') {
        errors.push('구분을 선택해주세요.');
      }

      if (!row.material || row.material.trim() === '') {
        errors.push('원료를 입력해주세요.');
      }

      // Mw 또는 Density 중 하나는 필수
      const hasMw = row.mw !== null && row.mw !== undefined && row.mw !== '';
      const hasDensity = row.density !== null && row.density !== undefined && row.density !== '';

      if (!hasMw && !hasDensity) {
        errors.push('분자량 또는 밀도 중 하나는 반드시 입력해주세요.');
      }

      // 소수점 검증
      if (hasMw) {
        const mwValue = parseFloat(row.mw);
        if (isNaN(mwValue) || mwValue <= 0) {
          errors.push('분자량은 0보다 큰 숫자를 입력해주세요.');
        } else {
          const mwStr = row.mw.toString();
          if (mwStr.includes('.') && mwStr.split('.')[1].length > 4) {
            errors.push('분자량은 소수점 넷째 자리까지만 입력 가능합니다.');
          }
        }
      }

      if (hasDensity) {
        const densityValue = parseFloat(row.density);
        if (isNaN(densityValue) || densityValue <= 0) {
          errors.push('밀도는 0보다 큰 숫자를 입력해주세요.');
        } else {
          const densityStr = row.density.toString();
          if (densityStr.includes('.') && densityStr.split('.')[1].length > 4) {
            errors.push('밀도는 소수점 넷째 자리까지만 입력 가능합니다.');
          }
        }
      }

      if (errors.length > 0) {
        this.showPopup(errors.join('\n'));
        return false;
      }

      return true;
    },

    async save() {
      if (this.isFetching) return;

      if (!this.validate()) {
        return;
      }

      this.isFetching = true;

      try {
        const rows = this.$refs.grid.getJsonRows();
        const updateData = rows[0];

        // 원료명을 키로 사용해서 업데이트
        await materialPropertyUpdate(this.editData.material, updateData);

        this.showPopup('수정이 완료되었습니다.', false, () => {
          this.$emit('closeTab');
          this.$emit('refreshList');
        });
      } catch (error) {
        console.error('수정 실패:', error);
        let errorMessage = '수정 중 오류가 발생했습니다.';

        // 백엔드 에러 메시지 추출
        if (error.response && error.response.data) {
          if (error.response.data.data) {
            errorMessage = error.response.data.data;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.errorMessage) {
            errorMessage = error.response.data.errorMessage;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }

        this.showPopup(errorMessage);
      } finally {
        this.isFetching = false;
      }
    },

    cancel() {
      this.$emit('closeTab');
    },

    showPopup(text, closable = false, callback) {
      this.alertMessage = text;
      this.alertCallback = callback;
      this.alertDialog = true;
    },

    handleAlertConfirm() {
      this.alertDialog = false;
      if (this.alertCallback && typeof this.alertCallback === 'function') {
        this.alertCallback();
      }
      this.alertCallback = null;
    },
  },

  components: {
    SetPopup,
    RealGrid,
  },
};
</script>

<style scoped>
.service {
  margin: auto;
  width: 100%;
}

.edit-section {
  margin-bottom: 30px;
}

.history-section {
  margin-top: 20px;
}

.material-readonly {
  background-color: #f5f5f5;
}

.material-readonly .v-input__slot {
  background-color: #f5f5f5 !important;
}

.filter-box {
  margin-bottom: 16px;
}

.material-property-edit {
  .v-input__slot {
    width: 100% !important;
  }
}

.btn-reset {
  background-color: #f5f5f5 !important;
  color: #666 !important;
}

.btn-search {
  background-color: #08800a !important;
  color: white !important;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
</style>
